import { Box, Tab, Tabs, Typography } from "@mui/material";
import { format, isValid, parseISO } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useProfile } from "../../../context/ProfileContext";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { useAllowedSectors, useDebounce } from "../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { ISinisterList, SinisterService } from "../../../shared/services/api/sinister/SinisterService";
import { useHandleSearch } from "../../sinister/hooks/useHandleSearch";
import { useSinisterFilters } from "../../sinister/hooks/useSinisterFilters";
import { useCollectionResponsibleOptions } from "../hooks/useCollectionResponsibleOptions";
import { SinisterSector, getSectorLabel } from "../constants/sectorOptions";
import { SinisterStage } from "../constants/stageOptions";
import { fieldsConfig } from "./fieldsConfig";
import { ToolbarSinisterGeneric } from "../../sinister/components/ToolbarSinisterGeneric/ToolbarSinisterGeneric";
import { TableCreditRecovery } from "./TableCreditRecovery";

export const CreditRecoveryListPresentation = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { debounce } = useDebounce();

	const filtersFromSearchParams = useSinisterFilters(searchParams, "Todos");
	const pagina = Number(searchParams.get("pagina") || "1");
	const limit = filtersFromSearchParams.limit;

	const [rows, setRows] = useState<ISinisterList[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [totalCount, setTotalCount] = useState(0);
	const [shouldFetch, setShouldFetch] = useState(true);
	const [selectedSector, setSelectedSector] = useState<SinisterSector>("cobranca");
	const [stageFilter, setStageFilter] = useState<SinisterStage | "all">("all");
	const [responsibleSearch, setResponsibleSearch] = useState("");
	const { isSuperAdmin } = useProfile();
	const hasViewPermission = isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.CreditRecoveryOptions, PermissionActionsEnum.View);

	// Abas visíveis conforme as permissões por setor do perfil — quem só atua no jurídico não entra em cobrança.
	const allowedSectors = useAllowedSectors(PermissionRolesEnum.CreditRecoveryOptions);
	const activeSector = allowedSectors.includes(selectedSector) ? selectedSector : allowedSectors[0];

	// As permissões chegam depois do primeiro render: se a aba inicial não for permitida, cai na primeira liberada.
	useEffect(() => {
		if (allowedSectors.includes(selectedSector)) return;

		setSelectedSector(allowedSectors[0]);
		setStageFilter("all");
		setShouldFetch(true);
	}, [allowedSectors, selectedSector]);

	// Opções do filtro "Responsável" já vêm filtradas por setor (aba ativa) — ver
	// PR-08-cobranca-responsavel-por-setor-frontend.md.
	const { responsibleOptions, loading: responsibleLoading } = useCollectionResponsibleOptions(activeSector, responsibleSearch);

	const dynamicFieldsConfig = useMemo(() => {
		return fieldsConfig.map((field) => {
			if (field.key === "selectedIdResponsible") {
				return {
					...field,
					extraProps: {
						...(field.extraProps || {}),
						options: responsibleOptions,
						loading: responsibleLoading,
						onInputChange: (_: any, val: string) => setResponsibleSearch(val),
					},
				};
			}
			return field;
		});
	}, [responsibleOptions, responsibleLoading]);

	const handleSearch = useHandleSearch(setSearchParams, setShouldFetch);

	const handleClearFilters = () => {
		setSearchParams({ status: filtersFromSearchParams.statusSinister.join(",") }, { replace: true });
		setShouldFetch(true);
	};

	useEffect(() => {
		if (!shouldFetch) return;

		setIsLoading(true);
		debounce(() => {
			SinisterService.getAllCobranca(
				pagina,
				limit,
				filtersFromSearchParams.textSearch,
				filtersFromSearchParams.createdFrom,
				filtersFromSearchParams.statusSinister.join(","),
				filtersFromSearchParams.typePeriod,
				filtersFromSearchParams.busyImobile,
				filtersFromSearchParams.filterField,
				filtersFromSearchParams.selectedIdClient,
				filtersFromSearchParams.dataIni,
				filtersFromSearchParams.dataFim,
				filtersFromSearchParams.selectedIdManager,
				filtersFromSearchParams.isLMI.toString(),
				filtersFromSearchParams.isSketch.toString(),
				"",
				selectedSector,
				stageFilter === "all" ? "" : stageFilter,
				filtersFromSearchParams.caseStatus === "Todos" ? "" : filtersFromSearchParams.caseStatus,
				filtersFromSearchParams.selectedIdResponsible
			).then((result) => {
				if (result instanceof Error) {
					alert(result.message);
				} else {
					setTotalCount(result.totalCount);
					setRows(result.data);
				}
				setIsLoading(false);
				setShouldFetch(false);
			});
		});
	}, [shouldFetch]);

	function getFiltersDescription(filters: typeof filtersFromSearchParams): string {
		const statusOptions = fieldsConfig.find((f) => f.key === "statusSinister")?.options ?? [];
		const translatedStatus = (filters.statusSinister || []).map((s) => statusOptions.find((opt) => opt.value === s)?.label || s).join(", ");
		const periodoTipoMap: Record<string, string> = { criacao: "Criação", abertura: "Abertura", limite: "Limite" };
		const periodoTipo = periodoTipoMap[filters.typePeriod] || filters.typePeriod;
		const dataIniFormat = isValid(parseISO(filters.dataIni)) ? format(parseISO(filters.dataIni), "dd/MM/yyyy") : "";
		const dataFimFormat = isValid(parseISO(filters.dataFim)) ? format(parseISO(filters.dataFim), "dd/MM/yyyy") : "";
		const periodo = dataIniFormat && dataFimFormat ? `no período de ${dataIniFormat} a ${dataFimFormat}` : "";
		const statusImovel =
			filters.busyImobile === "ocupado" ? "com imóvel ocupado" : filters.busyImobile === "desocupado" ? "com imóvel desocupado" : "em todos os imóveis";
		return `Exibindo sinistros ${periodo} (${periodoTipo}), ${statusImovel}, com status: ${translatedStatus}.`;
	}

	return (
		<>
			<LayoutBaseDePagina
				titulo="Cobrança"
				subtitulo="Listagem de sinistros com débito a recuperar"
				barraDeFerramentas={
					<>
						<Tabs
							value={activeSector}
							onChange={(_, newSector) => {
								setSelectedSector(newSector);
								setStageFilter("all");
								setShouldFetch(true);
							}}
							aria-label="Setor"
							sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper", mx: 1, borderRadius: 1 }}
						>
							{allowedSectors.map((sector) => (
								<Tab key={sector} value={sector} label={getSectorLabel(sector)} />
							))}
						</Tabs>

						<ToolbarSinisterGeneric
							fieldsConfig={dynamicFieldsConfig}
							filterInitialParams={filtersFromSearchParams}
							onSearch={handleSearch}
							onClear={handleClearFilters}
						/>
						<Box px={2} mt={1}>
							<Typography variant="body2" color="text.secondary">
								{getFiltersDescription(filtersFromSearchParams)}
							</Typography>
						</Box>
					</>
				}
			>
				{hasViewPermission && (
					<TableCreditRecovery
						rows={rows}
						totalCount={totalCount}
						page={pagina - 1}
						rowsPerPage={limit}
						selectedSector={activeSector}
						stageFilter={stageFilter}
						onStageFilterChange={(stage) => {
							setStageFilter(stage);
							setShouldFetch(true);
						}}
						onChangeSector={() => setShouldFetch(true)}
						onChangeStage={() => setShouldFetch(true)}
						onStatusUpdated={() => setShouldFetch(true)}
						onResponsibleUpdated={() => setShouldFetch(true)}
						onPageChange={(newPage) => {
							setSearchParams(
								(prev) => {
									const params = new URLSearchParams(prev);
									params.set("pagina", String(newPage + 1));
									return params;
								},
								{ replace: true }
							);
							setShouldFetch(true);
						}}
						onRowsPerPageChange={(newLimit) => {
							setSearchParams(
								(prev) => {
									const params = new URLSearchParams(prev);
									params.set("limit", String(newLimit));
									params.set("pagina", "1");
									return params;
								},
								{ replace: true }
							);
							setShouldFetch(true);
						}}
					/>
				)}
			</LayoutBaseDePagina>
			<LoadingComp isopen={isLoading} />
		</>
	);
};
